import React, { useState } from "react";
import { useTheme } from "styled-components";
import Modal, { ModalTransition } from "@atlaskit/modal-dialog";
import {
  AttendButton,
  Details,
  DetailsContainer,
  DetailWrapper,
  Divider,
  LocationContent,
  Subtitle,
  Title,
  TitleTextWrapper,
  Wrapper,
} from "./styles";
import AvatarGroup from "@atlaskit/avatar-group";
import { useAtlassianLocale } from "@Shared/Hooks/useAtlassianLocale";
import { buildDate, buildTime } from "../../../dateFormatting";
import { Date, Location, Time } from "./Icons";
import { LAYOUT_STRUCTURE_OPTIONS } from "../../../../../optics/components/drawers/settings/features/announcements/defaults";
import fakePeopleData from "../../../fakePeopleData";
import {
  getContentProperty,
  updateContentProperty,
} from "../../../announcements-api";
import { useTranslation } from "react-i18next";
import { useParticipants } from "../../hooks/use-participants";
import EditableText from "../../inline-edit/EditableText";
import AvatarListDialog from "./AvatarListDialog";
import { handleLinkClick } from "../../../../../helpers/handle-link-click";
import { urlValidator } from "../../../../../helpers/validators";

function Classic({ data, config, isEditable, viewMode, isTemplate = false }) {
  const { t } = useTranslation();
  const [currentUser, setCurrentUser] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [hasCurrentUserAccepted, setHasCurrentUserAccepted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useParticipants(
    { viewMode, data, config },
    { setParticipants, setCurrentUser, setHasCurrentUserAccepted }
  );

  const theme = useTheme();

  const [locale] = useAtlassianLocale();
  const { title, text, date, time, location, color, textColor } = data;
  const { borderRadius, layoutStructure } = config;

  const isList = config.view === "list";

  const hasLink = viewMode === "view" && !!data?.url?.link;

  const builtDate = buildDate(date, false, locale, config.designStyle);
  const builtTime = buildTime(time, config.dateFormat, locale);

  const handleAcceptInvite = async () => {
    try {
      setParticipants([{ ...currentUser }, ...participants]);
      setHasCurrentUserAccepted(true);
      const contentProperty = await getContentProperty(data.id);
      if (
        contentProperty.data.propertyExists &&
        !!contentProperty.data.body.results.length
      ) {
        const {
          value: { participants },
          version: { number },
        } = contentProperty.data.body.results[0];
        await updateContentProperty(
          `caelor-${data.id}`,
          {
            participants: [currentUser.key, ...participants],
          },
          number + 1
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleRevokeInvite = async () => {
    try {
      setParticipants([
        ...participants.filter(
          (participantId) => participantId.key !== currentUser.key
        ),
      ]);
      setHasCurrentUserAccepted(false);
      const contentProperty = await getContentProperty(data.id);
      if (
        contentProperty.data.propertyExists &&
        !!contentProperty.data.body.results.length
      ) {
        const {
          value: { participants },
          version: { number },
        } = contentProperty.data.body.results[0];
        await updateContentProperty(
          `caelor-${data.id}`,
          {
            participants: [
              ...participants.filter(
                (participantId) => participantId !== currentUser.key
              ),
            ],
          },
          number + 1
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const isLocationLink = urlValidator({ url: location });

  return (
    <>
      <Wrapper
        isTemplate={isTemplate}
        borderRadius={borderRadius}
        isList={isList}
        hasLink={hasLink}
        onClick={() => {
          if (hasLink) {
            handleLinkClick({
              url: data?.url || {},
              newTab: data?.newTab === "true",
            });
          }
        }}
      >
        <TitleTextWrapper>
          <Title>
            <EditableText
              announcementId={data.id}
              isEditable={isEditable}
              value={title}
              propKey="title"
              disableNewLine
            />
          </Title>
          {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.TEXT) && (
            <Subtitle>
              <EditableText
                announcementId={data.id}
                isEditable={isEditable}
                value={text}
                propKey="text"
              />
            </Subtitle>
          )}
        </TitleTextWrapper>
        <Divider isList={isList} />
        <DetailsContainer>
          {(layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.DATE) ||
            layoutStructure.includes(
              LAYOUT_STRUCTURE_OPTIONS.PARTICIPANTS
            )) && (
            <DetailWrapper clr={color[theme.name]} isActive>
              {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.DATE) ? (
                <Details>
                  <Date color={color[theme.name]} />
                  <span>{builtDate}</span>
                </Details>
              ) : (
                <div />
              )}

              {layoutStructure.includes(
                LAYOUT_STRUCTURE_OPTIONS.PARTICIPANTS
              ) && (
                <AvatarGroup
                  data={viewMode === "edit" ? fakePeopleData : participants}
                  appearance="stack"
                  size="medium"
                  onAvatarClick={(e) => {
                    e.preventDefault();
                  }}
                  onMoreClick={(e) => {
                    e.preventDefault();
                    if (viewMode === "view") {
                      setIsModalOpen(true);
                    }
                  }}
                />
              )}
            </DetailWrapper>
          )}
          {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.LOCATION) && (
            <DetailWrapper clr={color[theme.name]}>
              <Details>
                <Location color={color[theme.name]} />
                <LocationContent
                  style={{ cursor: isLocationLink ? "pointer" : "default" }}
                  onClick={() => {
                    if (isLocationLink) {
                      window.open(location, "_blank");
                    }
                  }}
                >
                  {location}
                </LocationContent>
              </Details>
            </DetailWrapper>
          )}
          {(layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.TIME) ||
            layoutStructure.includes(
              LAYOUT_STRUCTURE_OPTIONS.PARTICIPANTS
            )) && (
            <DetailWrapper clr={color[theme.name]}>
              {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.TIME) ? (
                <Details>
                  <Time color={color[theme.name]} />
                  <span>{builtTime}</span>
                </Details>
              ) : (
                <div />
              )}
              {layoutStructure.includes(
                LAYOUT_STRUCTURE_OPTIONS.PARTICIPANTS
              ) && (
                <AttendButton
                  bgClr={color[theme.name]}
                  textClr={textColor[theme.name]}
                  inverse={hasCurrentUserAccepted}
                  onClick={(e) => {
                    if (viewMode === "view") {
                      e.preventDefault();
                      e.stopPropagation();
                      hasCurrentUserAccepted
                        ? handleRevokeInvite()
                        : handleAcceptInvite();
                    }
                  }}
                >
                  {t(hasCurrentUserAccepted ? t("unattend") : t("attend"))}
                </AttendButton>
              )}
            </DetailWrapper>
          )}
        </DetailsContainer>
      </Wrapper>

      <ModalTransition>
        {isModalOpen && (
          <Modal
            width="200px"
            isBlanketHidden
            onClose={() => setIsModalOpen(false)}
          >
            <AvatarListDialog avatars={participants} />
          </Modal>
        )}
      </ModalTransition>
    </>
  );
}

export default Classic;
